/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Badge, Col, Row, Table, Tag } from "antd";
import {
  ClockIcon,
  PriorityIcon,
  RightIconBlue,
  RightIconBW,
  ToteIconGrey,
  TrolleyIconGrey,
} from "../../assets/icons/icons";
import Icon, { CaretDownFilled } from "@ant-design/icons";
import { convertUTCToLocal } from "../../utils/timeUtils";
import { useEffect, useState } from "react";
import ActivityLogs from "./activityLogs";

const FlowView = ({ d2, payload, setPayload }: any) => {
  // state
  const [expandedRowKeys, setExpandedRowKeys] = useState<any>([]);
  const [logOpen, setLogOpen] = useState({ open: false, details: null });

  const flattenTrolleyData = (data: any) => {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    // Check if this is the old structure (trolley_details with trolley_runs)
    const isOldStructure =
      data.length > 0 && data[0]?.trolley_runs !== undefined;

    if (isOldStructure) {
      // Old structure: trolley_details array with trolley_runs nested arrays
      const rows: any = [];
      data.forEach((trolley: any) => {
        const runs = trolley?.trolley_runs || [];
        if (runs.length === 0) {
          rows.push({
            ...trolley,
            movement: null,
          });
        } else {
          runs.forEach((run: any, index: any) => {
            rows.push({
              ...trolley,
              movement: run,
              _isFirstRow: index === 0,
              _rowSpan: runs.length,
            });
          });
        }
      });
      return rows;
    }

    // New response structure: data is already an array of movement runs
    // Each item represents a trolley movement run with associated totes
    return data.map((item: any) => {
      // Map new structure to expected format
      const isTrolley = item.entity_type === "trolley";
      const isTote = item.entity_type === "tote";
      
      return {
        ...item,
        // Map field names for backward compatibility
        // IMPORTANT: Only set trolley_id for trolleys, tote_id for totes
        trolley_id: isTrolley ? item.entity_id : undefined,
        tote_id: isTote ? item.entity_id : undefined,
        trolley_serial_id: isTrolley ? item.serial_id : undefined,
        tote_serial_id: isTote ? item.serial_id : undefined,
        type: isTrolley ? "Trolley" : isTote ? "Tote" : undefined,
        // Map associated_totes to tote_details for compatibility
        tote_details: item.associated_totes || [],
        // Movement data is now directly on the item as properties
        // We'll access them directly in the render functions
      };
    });
  };

  const inoutClock = () => {
    return (
      <Row
        gutter={10}
        style={{ color: "#fff", marginTop: "10px" }}
        align="middle"
      >
        <Col>
          <Icon component={() => ClockIcon("#fff")} />
        </Col>
        <Col>
          <div>In</div>
        </Col>
        <Col>
          <Icon component={() => RightIconBW("#fff")} />
        </Col>
        <Col>
          <div>Out</div>
        </Col>
      </Row>
    );
  };

  const renderFlow = (locationDetails: any) => {
    // Check if there's any meaningful data to display
    const hasData =
      locationDetails &&
      (locationDetails?.in_time ||
        locationDetails?.out_time ||
        locationDetails?.from_aisle ||
        locationDetails?.to_aisle ||
        locationDetails?.missed_scan ||
        locationDetails?.delayed_scan);

    if (!hasData) {
      return "-";
    }

    return (
      <div>
        <Row align="middle" justify="space-between">
          <Row gutter={10}>
            <Col>
              <a>{locationDetails?.from_aisle || "-"}</a>
            </Col>
            {locationDetails?.from_aisle && locationDetails?.to_aisle && (
              <Col>
                <Icon component={RightIconBlue} />
              </Col>
            )}
            <Col>
              <a>{locationDetails?.to_aisle || "-"}</a>
            </Col>
          </Row>

          {locationDetails &&
            (("missed_scan" in locationDetails &&
              locationDetails.missed_scan) ||
              ("delayed_scan" in locationDetails &&
                locationDetails.delayed_scan)) &&
            (locationDetails.missed_scan ? (
              <Tag color="red">
                <Badge dot color="red" style={{ marginRight: 5 }} />
                {"Missed Scan"}
              </Tag>
            ) : (
              <Tag color="orange">
                <Badge dot color="orange" style={{ marginRight: 5 }} />
                {"Delayed"}
              </Tag>
            ))}
        </Row>
        <Row
          gutter={10}
          style={{ marginTop: "10px", color: "#101010", fontWeight: "600" }}
        >
          <Col>
            <Icon component={() => ClockIcon("#101010")} />
          </Col>
          <Col>
            <div>
              {locationDetails?.in_time
                ? convertUTCToLocal(locationDetails?.in_time)
                : "-"}
            </div>
          </Col>
          <Col>
            <Icon component={() => RightIconBW("#101010")} />
          </Col>
          <Col>
            <div>
              {locationDetails?.out_time
                ? convertUTCToLocal(locationDetails?.out_time)
                : "-"}
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const columns: any = [
    {
      title: "Asset",
      key: "assetId",
      fixed: "left",
      width: 220,
      render: (record: any) => (
        <>
          <Row justify="space-between">
            <Row gutter={12}>
              <Col>
                <Icon
                  component={
                    record?.type === "Tote" ? ToteIconGrey : TrolleyIconGrey
                  }
                  onClick={() => setLogOpen({ open: true, details: record })}
                  style={{ cursor: "pointer" }}
                />
              </Col>
              <Col>
                <a>
                  {record?.type !== "Tote"
                    ? record?.trolley_serial_id || record?.serial_id
                    : record?.tote_serial_id || record?.serial_id}
                </a>
              </Col>
            </Row>
            <Row gutter={10}>
              {record?.type === "Trolley" &&
                record?.tote_details &&
                record?.tote_details?.length > 0 && (
                  <Col>
                    <Badge
                      count={record?.tote_details?.length}
                      color="#0285AD"
                      style={{
                        borderRadius: 3,
                        fontSize: "10px",
                      }}
                      size="small"
                    />
                  </Col>
                )}
              {(record?.priority === "High" || record?.priority === true) &&
                record?.priority !== "Standard" && (
                  <Col>
                    <Icon component={PriorityIcon} />
                  </Col>
                )}
              {record?.type === "Trolley" &&
                record?.tote_details?.length > 0 && (
                  <Col>
                    <CaretDownFilled
                      style={{
                        cursor: "pointer",
                        transition: "0.5s ease",
                        transform: (() => {
                          const entityId =
                            record.trolley_id || record.entity_id;
                          const movementRun = record.trolley_movement_run;
                          const rowId =
                            movementRun !== null && movementRun !== undefined
                              ? `${entityId}-${movementRun}`
                              : entityId;
                          return expandedRowKeys.includes(rowId)
                            ? "rotate(180deg)"
                            : "rotate(0deg)";
                        })(),
                      }}
                      onClick={(e) => {
                        // Use same key logic as rowKey for consistency
                        const entityId = record.trolley_id || record.tote_id || record.entity_id;
                        const movementRun = record.trolley_movement_run || record.tote_movement_run;
                        const rowId =
                          movementRun !== null && movementRun !== undefined
                            ? `${entityId}-${movementRun}`
                            : entityId;
                        expandedRowKeys.includes(rowId)
                          ? setExpandedRowKeys([])
                          : setExpandedRowKeys([rowId]);
                        e.stopPropagation();
                      }}
                    />
                  </Col>
                )}
            </Row>
          </Row>
          <Row gutter={10} style={{ marginTop: "10px" }}>
            <Col>
              <Icon component={() => ClockIcon("#101010")} />
            </Col>
            <Col>
              <div style={{ color: "#101010", fontWeight: "600" }}>
                {record?.registered_on
                  ? convertUTCToLocal(record?.registered_on)
                  : "-"}
              </div>
            </Col>
          </Row>
        </>
      ),
    },
    {
      title: (
        <>
          <Row justify="space-between" align="middle">
            <div>Airlog Packing</div>
            <label>(Target Time: 1.5 hours)</label>
          </Row>
          {inoutClock()}
        </>
      ),
      width: 350,
      key: "airlogPacking",
      render: (record: any) => {
        // New structure: direct property access
        return renderFlow(record?.airlog_packing || record?.movement?.[0]);
      },
    },
    {
      title: (
        <>
          <Row justify="space-between" align="middle">
            <div>Airlog Dispatch Dock</div>
            <label>(Target Time: 1 hour)</label>
          </Row>
          {inoutClock()}
        </>
      ),
      width: 350,
      key: "airlogDispatchDock",
      render: (record: any) => {
        // New structure: direct property access
        return renderFlow(
          record?.airlog_dispatch_dock || record?.movement?.[1]
        );
      },
    },
    {
      title: (
        <>
          <Row justify="space-between" align="middle">
            <div>FAL Proximity Store (L80)</div>
            <label>(Target Time: 2 hours)</label>
          </Row>
          {inoutClock()}
        </>
      ),
      width: 350,
      key: "falProximityStore",
      render: (record: any) => {
        // New structure: direct property access
        return renderFlow(record?.fal_proximity_store || record?.movement?.[2]);
      },
    },

    {
      title: (
        <>
          <Row justify="space-between" align="middle">
            <div>FAL PoU</div>
            <label>(Target Time: 3 hours)</label>
          </Row>
          {inoutClock()}
        </>
      ),
      width: 350,
      key: "falPoU",
      render: (record: any) => {
        // New structure: direct property access
        return renderFlow(record?.fal_pou || record?.movement?.[3]);
      },
    },
    {
      title: (
        <>
          <Row justify="space-between" align="middle">
            <div>Airlog Empty Holding</div>
            <label>(Target Time: 6.5 hours)</label>
          </Row>
          {inoutClock()}
        </>
      ),
      width: 350,
      key: "airlog_empty_holding",
      render: (record: any) => {
        // New structure: direct property access
        return renderFlow(
          record?.airlog_empty_holding || record?.movement?.[4]
        );
      },
    },
  ];

  const expandedRowRender = (record: any) => {
    const toteDetails = record?.tote_details || record?.associated_totes || [];
    if (!toteDetails.length) return null;

    const toteColumns = columns.map((col: any, index: number) => {
      if (index === 0) {
        return {
          ...col,
          render: (tote: any) => (
            <>
              <Row justify="space-between">
                <Row gutter={12}>
                  <Col>
                    <Icon
                      component={ToteIconGrey}
                      onClick={() => {
                        setLogOpen({
                          open: true,
                          details: tote,
                        });
                      }}
                      style={{ cursor: "pointer" }}
                    />
                  </Col>
                  <Col>
                    <a
                      onClick={() => {
                        setLogOpen({
                          open: true,
                          details: tote,
                        });
                      }}
                    >
                      {tote?.tote_serial_id || tote?.serial_id}
                    </a>
                  </Col>
                  {(tote?.priority === "High" || tote?.priority === true) &&
                    tote?.priority !== "Standard" && (
                      <Col>
                        <Icon component={PriorityIcon} />
                      </Col>
                    )}
                </Row>
              </Row>
              <Row gutter={10} style={{ marginTop: "10px" }}>
                <Col>
                  <Icon component={() => ClockIcon("#101010")} />
                </Col>
                <Col>
                  <div style={{ color: "#101010", fontWeight: "600" }}>
                    {tote?.registered_on
                      ? convertUTCToLocal(tote?.registered_on)
                      : "-"}
                  </div>
                </Col>
              </Row>
            </>
          ),
        };
      }
      return {
        ...col,
        title: "",
        render: (tote: any) => {
          const keyMap: any = {
            airlogPacking: "airlog_packing",
            airlogDispatchDock: "airlog_dispatch_dock",
            falProximityStore: "fal_proximity_store",
            falPoU: "fal_pou",
            airlog_empty_holding: "airlog_empty_holding",
          };

          const flowKey = keyMap[col.key];
          return renderFlow(tote?.[flowKey]);
        },
      };
    });

    return (
      <Table
        columns={toteColumns}
        bordered
        scroll={{ x: "95vw" }}
        rowClassName="expandedRow"
        rowHoverable={false}
        style={{ marginTop: 0 }}
        className="dashboardTable"
        dataSource={toteDetails}
        pagination={false}
        showHeader={false}
        rowKey={(row) =>
          row?.tote_movement_id || row?.tote_id || row?.tote_serial_id
        }
      />
    );
  };

  useEffect(() => {
    if (localStorage?.getItem("flowFilter")) {
      const dataArray = d2?.data || d2?.trolley_details || [];
      const firstItem = dataArray[0];
      if (!firstItem) return;

      const toteDetails =
        firstItem?.associated_totes || firstItem?.tote_details || [];
      if (toteDetails.length > 0) {
        // Use same key logic as rowKey for consistency
        const entityId = firstItem?.trolley_id || firstItem?.tote_id || firstItem?.entity_id;
        const movementRun = firstItem?.trolley_movement_run || firstItem?.tote_movement_run;
        const rowId =
          movementRun !== null && movementRun !== undefined
            ? `${entityId}-${movementRun}`
            : entityId;
        if (rowId) {
          setExpandedRowKeys([rowId]);
        }
      }
    }
  }, [localStorage, d2]);

  return (
    <>
      {logOpen?.open && <ActivityLogs open={logOpen} setOpen={setLogOpen} />}
      <Table
        columns={columns}
        locale={{ emptyText: "No data Available" }}
        dataSource={flattenTrolleyData(d2?.data || d2?.trolley_details)}
        className="dashboardTable"
        scroll={{ x: "95vw", y: "57vh" }}
        bordered
        expandable={{
          expandedRowRender: (record: any) => expandedRowRender(record),
          expandIconColumnIndex: -1,
        }}
        expandedRowKeys={expandedRowKeys}
        onExpand={(expanded, record) => {
          // Use same key logic as rowKey for consistency
          const entityId = record.trolley_id || record.tote_id || record.entity_id;
          const movementRun = record.trolley_movement_run || record.tote_movement_run;
          const rowId =
            movementRun !== null && movementRun !== undefined
              ? `${entityId}-${movementRun}`
              : entityId;
          setExpandedRowKeys(expanded ? [rowId] : []);
        }}
        rowKey={(record) => {
          // For trolleys with multiple runs, use entity_id + movement_run
          // For totes, use tote_id
          const entityId = record?.trolley_id || record?.tote_id || record?.entity_id;
          const movementRun = record?.trolley_movement_run || record?.tote_movement_run;
          if (movementRun !== null && movementRun !== undefined) {
            return `${entityId}-${movementRun}`;
          }
          return entityId || `${record?.entity_id}-${movementRun || 0}`;
        }}
        pagination={{
          current: payload.page_no,
          pageSize: payload.page_size,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 25, 50, 100],
          total: d2?.total_items || d2?.total_pages * payload.page_size || 0,
          onChange: (page, pageSize) => {
            setPayload({
              ...payload,
              page_no: payload?.page_size !== pageSize ? 1 : page,
              page_size: pageSize,
            });
          },
        }}
      />
    </>
  );
};

export default FlowView;
